import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useUser } from '@/app/redux/hooks/user';
import {
  getAndClearStudentCreationCallback,
  refreshModals,
  selectModals,
  setAttemptedProFeature,
  setContactFormModalOpen,
  setStudentCreationModalOpen,
  setTrialEndedModalOpen,
} from '@/app/redux/slices/modalsSlice';
import ConfirmationDialogue from '@/common/ui/modals/ConfirmationDialogue';
import OnboardingDialogue from '@/common/ui/modals/OnboardingDialogue';
import OverSeatLimitModal from '@/common/ui/modals/OverSeatLimitModal';
import PaymentOverdueModal from '@/common/ui/modals/PaymentOverdueModal';
import ProFeatureModal from '@/common/ui/modals/ProFeatureModal';
import TrialEndedModal from '@/common/ui/modals/TrialEndedModal';
import {
  getPaymentPastDueStatus,
  getSeatLimitStatus,
} from '@/common/utils/license';
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';
import EmailFormDialog from '@/dashboard/EmailFormDialog';
import StudentCreationModal from '@/dashboard/StudentCreationModal';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const WHITELIST = [
  '/login',
  '/signup',
  '/onboarding',
  '/invite',
  '/verify',
  '/pricing',
  '/checkout',
];

export default function ModalProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const currentPath = location.pathname;

  const {
    renderCount,
    contactFormModalOpen,
    confirmationDialogue,
    attemptedProFeature,
    trialEndedModalOpen,
    studentCreationModalOpen,
  } = useAppSelector(selectModals);

  const { organisation } = useOrganisation();
  const license = organisation?.license;
  const licenseStatus = license?.status;
  const trialEndDate = license?.trialEndDate;
  const activeSeats = organisation?.studentCount;

  const { user } = useUser();
  const hasOrganisation = !!user?.organisationIds.length;
  const onboardingComplete = user?.onboardingCompleted ?? false;
  const showOnboardingDialogue = hasOrganisation && !onboardingComplete;

  const [paymentOverdue, setPaymentOverdue] = useState(false);
  const [overSeatLimit, setOverSeatLimit] = useState(false);

  const isPathWhitelisted = useCallback(
    () => WHITELIST.some((p) => currentPath.includes(p)),
    [currentPath],
  );

  useEffect(() => {
    if (isPathWhitelisted()) {
      return;
    }

    if (showOnboardingDialogue) return;

    if (licenseStatus === 'past_due') {
      setPaymentOverdue(getPaymentPastDueStatus(license) === 'grace-ended');
    } else if (licenseStatus === 'active') {
      setPaymentOverdue(false);
      setOverSeatLimit(
        getSeatLimitStatus(license, activeSeats) === 'grace-ended',
      );
    }
  }, [
    isPathWhitelisted,
    renderCount,
    licenseStatus,
    trialEndDate,
    activeSeats,
    license,
    showOnboardingDialogue,
  ]);

  useEffect(() => {
    if (!organisation) return;

    const hasViewedTrialEndedModal = getFromLocalStorage(
      `trialEndedSeen_${organisation.id}`,
    );
    if (hasViewedTrialEndedModal) return;

    if (organisation.license.plan === 'free') {
      dispatch(setTrialEndedModalOpen(true));
    }
  }, [dispatch, renderCount, organisation]);

  return (
    <>
      {contactFormModalOpen && (
        <EmailFormDialog
          isOpen={contactFormModalOpen}
          onClose={() => {
            dispatch(setContactFormModalOpen(false));
            dispatch(refreshModals());
          }}
        />
      )}
      {paymentOverdue && <PaymentOverdueModal />}
      {overSeatLimit && <OverSeatLimitModal setOpen={setOverSeatLimit} />}
      {!isPathWhitelisted() && trialEndedModalOpen && (
        <TrialEndedModal
          onClose={() => {
            if (organisation)
              saveToLocalStorage(`trialEndedSeen_${organisation.id}`, true);
            dispatch(setTrialEndedModalOpen(false));
            dispatch(refreshModals());
          }}
        />
      )}
      {confirmationDialogue && <ConfirmationDialogue />}
      {studentCreationModalOpen && (
        <StudentCreationModal
          open={studentCreationModalOpen}
          onClose={(studentId) => {
            dispatch(setStudentCreationModalOpen(false));
            const callback = getAndClearStudentCreationCallback();
            callback?.(studentId);
          }}
        />
      )}
      {!isPathWhitelisted() && showOnboardingDialogue && <OnboardingDialogue />}
      {!isPathWhitelisted() && attemptedProFeature && (
        <ProFeatureModal
          feature={attemptedProFeature}
          onClose={() => dispatch(setAttemptedProFeature(null))}
        />
      )}
      {children}
    </>
  );
}
